import type { LaddersMove, LaddersPlayerCount, LaddersView } from "@bing/engine-ladders";
import { laddersOnlineRoom, laddersPrivateRoom } from "@bing/shared";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { LaddersTable } from "../screens/LaddersTable";
import {
  DEFAULT_LADDERS_SEATS,
  getLaddersPublicSeats,
  laddersSeatsOrDefault,
} from "../settings/prefs";
import { useSession } from "../store/context";
import { LaddersEndOverlay } from "./LaddersGame";
import type { LobbyPlayer } from "./online-connection";
import { OnlineGame } from "./OnlineGame";
import type { OnlineJoin } from "./private-table";
import type { SeatPlayer } from "./seat-identity";

/**
 * The size a CREATE asks for: the host's pick on «تنظیمات میز» (the intent's `seats`), else four — a
 * table created without the settings screen (a friend invited from the list) is the default table.
 * Every other way in (a quick-match, a join by id, a re-join) names no private room at all: the
 * table already exists, whatever its size.
 */
export function laddersSeatsOfJoin(join: OnlineJoin): LaddersPlayerCount {
  return join.kind === "create" ? laddersSeatsOrDefault(join.seats) : DEFAULT_LADDERS_SEATS;
}

/** The lobby roster as the table and the result card both want it — ONE mapping, so a chair is
 *  never a named person on the board and a nameless «حریف N» on the end card. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly SeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    // The server resolves the display name; `nickname` is what `displayName()` reads it from.
    nickname: p.name,
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/**
 * Online مار پله — a thin wrapper over the shared `OnlineGame` shell: a public quick-match or a host's
 * private table, perfect information, no deck, no teams.
 *
 * THE TABLE'S SIZE IS ITS ROOM, ON BOTH DOORS — the پاسور wiring, never منچ's derived name: a room's
 * `maxClients` is fixed at the game-server's `define()`, so each size is its own room type
 * (`laddersOnlineRoom` / `laddersPrivateRoom`, shared with the server's `define()`). A public
 * quick-match asks for the size the lobby's tap chose (`getLaddersPublicSeats`, the courier); a
 * host's own table the size picked on «تنظیمات میز».
 * ⚠️ `privateRoom` MUST BE STATED: `OnlineGame` would otherwise derive it from the PUBLIC name, so a
 * host whose last public tap was «۲ نفره» would get a table for two whatever they picked.
 */
export function LaddersOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  const onlineJoin = useSession((s) => s.onlineJoin);
  return (
    <OnlineGame<LaddersMove, LaddersView>
      gameId="ladders"
      room={laddersOnlineRoom(getLaddersPublicSeats())}
      privateRoom={laddersPrivateRoom(laddersSeatsOfJoin(onlineJoin))}
      title={fa.ladders.title}
      renderTable={({ view, legal, send, leave, players, timer }) => (
        <LaddersTable
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          players={seatPlayers(players)}
          timer={timer}
        />
      )}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <LaddersEndOverlay
          result={result}
          view={view}
          // the SAME identities the table's strips get
          self={profile}
          players={seatPlayers(players)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
